import { FC } from 'react'
import { useRouter } from 'next/router'

import Section from '@/components/atomic/molecules/Section'
import ReviewTemplate from '@/features/review/components/review-template'
import { ReviewSectionTitle } from '@/features/review/components/review-section-title'
import { getReviewVisibility } from '@/features/review/utils/review-visibility'
import { postAmplitude } from '@/core/lib'
import { useTranslation } from '@/lang'
import { AirbridgeEvents, sendAirbridgeEvent } from '@/utils/airbridge'
import { Locales } from '@/utils/language'
import { User } from '@/features/member/member.type'

import type { ReviewsPaginationResponseInternal } from '@/features/review/api/review.type'
import type { HospitalView } from '@/features/hospital/types/hospital.type'

interface HospitalReviewSectionProps {
  reviewWithPagination?: ReviewsPaginationResponseInternal
  hospital: HospitalView
  isAnonymousUser: boolean
  locale: Locales
  commonAmplitudeProperties: {
    hospital_id: number
    hospital_name: string
    hospital_region: string
  }
}

export const HospitalReviewSection: FC<HospitalReviewSectionProps> = ({
  reviewWithPagination,
  hospital,
  isAnonymousUser,
  locale,
  commonAmplitudeProperties,
}) => {
  const { t } = useTranslation()
  const router = useRouter()

  const { isLocked } = getReviewVisibility({
    user: isAnonymousUser ? undefined : ({ id: -1 } as User),
    locale,
  })

  const totalCount = reviewWithPagination?.totalElementNumber || hospital.statistic.reviewCount

  return (
    <Section padding={26} className="border-t border-gray-200" style={{ padding: '26px' }}>
      <ReviewTemplate
        title={<ReviewSectionTitle title={t('templates.hospitalDetail.reviewTitle')} totalCount={totalCount} />}
        totalCount={totalCount}
        reviews={reviewWithPagination?.contents || []}
        isLocked={isLocked}
        moreLink={{
          href: `/reviews?hospitalId=${hospital.hospitalId}`,
          onClick: () => {
            postAmplitude('click_more_review_in_hospital_detail', commonAmplitudeProperties)
          },
        }}
        onReviewClick={() => {
          sendAirbridgeEvent(AirbridgeEvents.REVIEW_HOSPITALPAGE_CLICK, {
            semanticAttributes: {
              products: [
                {
                  brandID: hospital.hospitalId,
                  brandName: hospital.name,
                },
              ],
            },
          })
        }}
        onLoginClick={() => {
          sendAirbridgeEvent(AirbridgeEvents.SIGN_UP_BUTTON_CLICK_IN_WEB, {})
          postAmplitude('click_login_review_module_in_hospital_detail', commonAmplitudeProperties)

          const query = { return_url: `${location.pathname}${decodeURI(location.search)}` }
          router.push({ pathname: `/signin`, query })
        }}
      />
    </Section>
  )
}

export default HospitalReviewSection
